import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMyLeagues, getCurrentEvent, getNextEvent, getLeaguePickStatus, getGlobalPool, enterTitleShot, getMyStats, getCommunityStats, getFights } from '../services/api';
import Loading from '../components/Loading';
import NextEventCard from '../components/NextEventCard';
import PullToRefresh from '../components/PullToRefresh';
import MainEventHero from '../components/MainEventHero';
import CardChip from '../components/CardChip';
import { shareOrCopy } from '../utils/share';
import '../styles/app/dashboard.css';
import { HIDE_MONEY } from '../utils/platform';

function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pools, setPools] = useState([]);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [nextEvent, setNextEvent] = useState(null);
  const [pickStatus, setPickStatus] = useState({}); // { poolId: { submitted: true/false } }
  const [loading, setLoading] = useState(true);
  const [globalPool, setGlobalPool] = useState(null);
  const [enteringTitleShot, setEnteringTitleShot] = useState(false);
  const [shareToast, setShareToast] = useState('');
  const [myStats, setMyStats] = useState(null);
  const [eventPulse, setEventPulse] = useState(null); // { totalPicks, mainEventTopFighter, mainEventTopPercent }

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [poolsRes, eventRes, globalPoolRes, statsRes] = await Promise.all([
        getMyLeagues(),
        getCurrentEvent().catch(() => ({ data: null })),
        getGlobalPool().catch(() => ({ data: null })),
        getMyStats().catch(() => ({ data: null }))
      ]);
      if (statsRes.data) setMyStats(statsRes.data);
      const globalId = globalPoolRes.data?.id;
      setPools(globalId ? (poolsRes.data || []).filter(p => p.id !== globalId) : poolsRes.data);
      setCurrentEvent(eventRes.data);
      setGlobalPool(globalPoolRes.data);

      // If current event is completed, fetch next event
      if (eventRes.data?.status === 'completed') {
        const nextRes = await getNextEvent().catch(() => ({ data: null }));
        setNextEvent(nextRes.data);
      }

      // Fetch community pulse for the current event (for the Title Shot card)
      if (eventRes.data?.id && eventRes.data.status !== 'completed') {
        try {
          const [statsRes, fightsRes] = await Promise.all([
            getCommunityStats(eventRes.data.id),
            getFights(eventRes.data.id)
          ]);
          const stats = statsRes.data || [];
          const fights = (fightsRes.data || []).filter(f => !f.is_cancelled);
          const main = fights.filter(f => f.card_type === 'main')
            .sort((a, b) => (a.fight_order || 0) - (b.fight_order || 0))[0];
          const totalPicks = stats.reduce((sum, s) => sum + (s.total_picks || 0), 0);
          let topFighter = null;
          let topPercent = 0;
          if (main) {
            const mainStat = stats.find(s => s.fight_id === main.id);
            if (mainStat) {
              if (mainStat.fighter1_percentage >= mainStat.fighter2_percentage) {
                topFighter = main.fighter1_name;
                topPercent = mainStat.fighter1_percentage;
              } else {
                topFighter = main.fighter2_name;
                topPercent = mainStat.fighter2_percentage;
              }
            }
          }
          setEventPulse({ totalPicks, topFighter, topPercent });
        } catch (e) { /* silent */ }
      }

      // Fetch pick status for each pool if there's an active event
      const allPools = [...(poolsRes.data || [])];
      if (globalPoolRes.data?.id && globalPoolRes.data?.user_entered) {
        allPools.push({ id: globalPoolRes.data.id });
      }
      if (eventRes.data && eventRes.data.status === 'open' && allPools.length > 0) {
        const statusPromises = allPools.map(pool =>
          getLeaguePickStatus(pool.id, eventRes.data.id)
            .then(res => ({ poolId: pool.id, status: res.data }))
            .catch(() => ({ poolId: pool.id, status: {} }))
        );
        const statuses = await Promise.all(statusPromises);
        const statusMap = {};
        statuses.forEach(({ poolId, status }) => {
          // Convert submissions array to object keyed by user_id
          const userStatusMap = {};
          if (status.submissions) {
            status.submissions.forEach(s => {
              userStatusMap[s.user_id] = { submitted: s.submitted, picks_count: s.picks_count, expected_count: s.expected_count };
            });
          }
          statusMap[poolId] = userStatusMap;
        });
        setPickStatus(statusMap);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };


  if (loading) return <Loading />;

  const isNewUser = pools.length === 0;

  const handleEnterTitleShot = async (e) => {
    e.stopPropagation();
    setEnteringTitleShot(true);
    try {
      await enterTitleShot();
      fetchData();
    } catch (err) {
      console.error('Failed to enter Title Shot:', err);
      alert('Failed to enter Title Shot. Please try again.');
    } finally {
      setEnteringTitleShot(false);
    }
  };

  const handleShareTitleShot = async (e) => {
    e.stopPropagation();
    const username = user?.username || 'Someone';
    const status = await shareOrCopy({
      title: 'PNP Title Shot',
      text: `@${username} just challenged you to the PNP Title Shot — free MMA pick'em pool. Pick the winner and method for every fight on the card.`,
      url: 'https://picknpray.com/join/TITLESHOT',
    });
    if (status === 'copied') {
      setShareToast('Link copied');
      setTimeout(() => setShareToast(''), 1800);
    } else if (status === 'error') {
      setShareToast('Couldn\'t share');
      setTimeout(() => setShareToast(''), 1800);
    }
  };

  return (
    <PullToRefresh onRefresh={fetchData}>
    <div className="container dash">

      {/* Personal welcome + stats banner */}
      <header className="dash-welcome pnp-rise">
        <div style={{ minWidth: 0 }}>
          <p className="pnp-eyebrow">Welcome back</p>
          <h1 className="pnp-title">{user?.username?.toUpperCase()}</h1>
          {!myStats || myStats.totalPicks === 0 ? (
            <p className="dash-welcome-sub">
              Make your picks and climb the leaderboard
            </p>
          ) : null}
        </div>
      </header>

      {/* Hero fight card for current event */}
      {currentEvent && <MainEventHero event={currentEvent} />}

      <div className="dash-grid">
        {/* Stats strip — your form */}
        {myStats && myStats.totalPicks > 0 && (
          <section className="dash-area-stats pnp-rise" style={{ '--i': 1 }}>
            <div className="dash-block-head">
              <p className="pnp-eyebrow pnp-eyebrow-mute">Your form</p>
            </div>
            <div className="dash-stats">
              <div className="pnp-stats">
                <StatCell label="Picks" value={myStats.totalPicks.toLocaleString()} />
                <StatCell label="Events" value={myStats.eventsPicked || 0} />
                <StatCell
                  label="Accuracy"
                  value={myStats.scoredPicks > 0 ? `${myStats.accuracy}%` : '—'}
                  accent={myStats.scoredPicks > 0}
                />
                <StatCell label="Correct" value={myStats.correctPicks || 0} />
              </div>
            </div>
          </section>
        )}

        {/* Show next event card when current is completed */}
        {currentEvent?.status === 'completed' && nextEvent && (
          <section className="dash-area-next">
            <NextEventCard event={nextEvent} />
          </section>
        )}

        {/* Public Pools Section */}
        <section className="dash-area-public pnp-rise" style={{ '--i': 2 }}>
          <div className="dash-block-head">
            <p className="pnp-eyebrow pnp-eyebrow-gold">Public Pools</p>
          </div>
          {globalPool && (
            <div
              className="dash-titleshot pnp-press"
              role="link"
              tabIndex={0}
              onClick={() => navigate(`/pool/${globalPool.id}`)}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/pool/${globalPool.id}`); }}
            >
              {shareToast && <div className="dash-toast">{shareToast}</div>}
              <div className="dash-titleshot-row">
                <div className="dash-titleshot-belt">
                  <img src="/images/title-belt.svg" alt="Title Belt" />
                </div>
                <div className="dash-titleshot-body">
                  <div className="dash-titleshot-name">PNP <em>Title Shot</em></div>
                  <p className="dash-titleshot-copy">
                    Pick the winner and method for every fight on the card
                  </p>
                  {globalPool.current_event ? (
                    <>
                      <p className="dash-titleshot-event">
                        {globalPool.current_event.status === 'locked' && <span className="nav-live-badge">LIVE</span>}
                        <span>
                          {globalPool.current_event.name} · <span className="pnp-num">{globalPool.event_entrants || 0}</span> competitor{globalPool.event_entrants !== 1 ? 's' : ''}
                        </span>
                      </p>
                      {eventPulse?.topFighter && eventPulse.topPercent > 50 && (
                        <p className="dash-titleshot-pulse">
                          <span className="pnp-num">{eventPulse.topPercent}%</span> picking {eventPulse.topFighter} in main event
                        </p>
                      )}
                    </>
                  ) : globalPool.next_event && (
                    <p className="dash-titleshot-pulse">
                      Up next: {globalPool.next_event.name} · {new Date(globalPool.next_event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                  )}
                </div>
              </div>

              {/* Status bar — mirrors the private pool rows below */}
              {(() => {
                const titleShotStatus = pickStatus[globalPool.id]?.[user?.id];
                const titleShotAllIn = titleShotStatus?.picks_count >= titleShotStatus?.expected_count && titleShotStatus?.expected_count > 0;
                const titleShotPartial = titleShotStatus?.picks_count > 0 && !titleShotAllIn;
                const isLiveEvent = globalPool.current_event?.status === 'locked';

                let dotColor, label;
                if (isLiveEvent) {
                  dotColor = 'var(--success)'; label = 'Live';
                } else if (!globalPool.current_event) {
                  dotColor = 'var(--text-muted)'; label = globalPool.next_event ? 'Upcoming' : 'No Active Event';
                } else if (!globalPool.user_entered) {
                  dotColor = 'var(--gold)'; label = 'Not Entered';
                } else if (titleShotAllIn) {
                  dotColor = 'var(--success)'; label = 'Picks In';
                } else if (titleShotPartial) {
                  dotColor = 'var(--gold)'; label = `${titleShotStatus.picks_count}/${titleShotStatus.expected_count}`;
                } else {
                  dotColor = 'var(--primary)'; label = 'Pending';
                }

                return (
                  <div className="dash-statusbar">
                    <span className="pnp-status" style={{ color: dotColor }}>
                      <span className="pnp-status-dot" />
                      {label}
                    </span>
                    <div className="dash-titleshot-actions">
                      {!globalPool.current_event ? (
                        <Link
                          to={`/pool/${globalPool.id}`}
                          className="btn btn-gold btn-sm"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Join Now
                        </Link>
                      ) : globalPool.current_event.status === 'locked' ? (
                        <Link
                          to={`/pool/${globalPool.id}/leaderboard`}
                          className="btn btn-gold btn-sm"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Leaderboard
                        </Link>
                      ) : globalPool.user_entered ? (
                        (() => {
                          const status = pickStatus[globalPool.id]?.[user?.id];
                          const allIn = status?.picks_count >= status?.expected_count && status?.expected_count > 0;
                          return allIn ? null : (
                            <Link
                              to={`/pool/${globalPool.id}/picks`}
                              className="btn btn-gold btn-sm"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {status?.picks_count > 0 ? `${status.picks_count}/${status.expected_count} Picks` : 'Make Picks'}
                            </Link>
                          );
                        })()
                      ) : (
                        <button
                          className="btn btn-gold btn-sm"
                          disabled={enteringTitleShot}
                          onClick={handleEnterTitleShot}
                        >
                          {enteringTitleShot ? 'Entering...' : 'Enter Title Shot'}
                        </button>
                      )}
                      <button
                        type="button"
                        aria-label="Share PNP Title Shot"
                        onClick={handleShareTitleShot}
                        className="dash-share"
                      >
                        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="18" cy="5" r="3" />
                          <circle cx="6" cy="12" r="3" />
                          <circle cx="18" cy="19" r="3" />
                          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </section>

        <section className="dash-area-pools pnp-rise" style={{ '--i': 3 }}>
          <div className="dash-block-head">
            <p className="pnp-eyebrow">My Pools</p>
            <div className="dash-actions">
              {isNewUser && (
                <span className="get-started-hint-inline">
                  Get started! <span className="bounce-arrow-right">→</span>
                </span>
              )}
              <Link to="/join" className="btn btn-secondary btn-sm">Join Pool</Link>
              <Link to="/create-pool" className="btn btn-primary btn-sm">Create Pool</Link>
            </div>
          </div>

          {pools.length === 0 ? (
            <div className="pnp-empty dash-empty">
              <div className="pnp-empty-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <h3 className="pnp-empty-title">No Pools Yet</h3>
              <p>
                Create your own pool or join an existing one with an invite code.
              </p>
              <div className="pnp-empty-actions">
                <Link to="/join" className="btn btn-secondary">Join a Pool</Link>
                <Link to="/create-pool" className="btn btn-primary">Create a Pool</Link>
              </div>
            </div>
          ) : (
            <div className="dash-pools">
              {pools.map((pool, index) => {
                const status = pickStatus[pool.id]?.[user?.id];
                const allIn = status?.picks_count >= status?.expected_count && status?.expected_count > 0;
                const hasPartialPicks = status?.picks_count > 0 && !allIn;
                const isPicksOpen = currentEvent?.status === 'open';
                const isLive = currentEvent?.status === 'locked';
                const accentColor = poolAccentColor(pool.id);

                const dotColor = isLive ? 'var(--success)' : allIn ? 'var(--success)' : hasPartialPicks ? 'var(--gold)' : 'var(--primary)';
                const statusLabel = isLive ? 'Live' : allIn ? 'Picks In' : hasPartialPicks ? `${status.picks_count}/${status.expected_count}` : 'Pending';

                return (
                  <div
                    key={pool.id}
                    className="dash-pool pnp-rise"
                    style={{ '--accent': accentColor, '--i': index + 4 }}
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate(isLive ? `/pool/${pool.id}/leaderboard` : `/pool/${pool.id}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(isLive ? `/pool/${pool.id}/leaderboard` : `/pool/${pool.id}`); }}
                  >
                    <div className="dash-pool-row">
                      <span className="dash-pool-mark" aria-hidden="true">
                        {(pool.name || '?').trim().charAt(0).toUpperCase()}
                      </span>
                      <div className="dash-pool-info">
                        <div className="dash-pool-name">
                          <span>{pool.name.toUpperCase()}</span>
                          {!HIDE_MONEY && pool.entry_fee > 0 && !!pool.has_paid && <CardChip tone="green">Paid</CardChip>}
                          {pool.commissioner_id === user?.id && <CardChip tone="mute">Commish</CardChip>}
                        </div>
                        <div className="dash-pool-meta">
                          <span><span className="pnp-num">{pool.member_count}</span> members</span>
                          {!HIDE_MONEY && pool.entry_fee > 0 && (
                            <>
                              <span className="dash-dot-sep">·</span>
                              <span><span className="pnp-num">${pool.entry_fee}</span> buy-in</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Pick status bar */}
                    <div className="dash-statusbar">
                      <span className="pnp-status" style={{ color: dotColor }}>
                        <span className="pnp-status-dot" />
                        {statusLabel}
                      </span>
                      {isPicksOpen && !allIn ? (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={(e) => { e.stopPropagation(); navigate(`/pool/${pool.id}/picks`); }}
                        >
                          {hasPartialPicks ? 'Finish' : 'Make Picks'}
                        </button>
                      ) : isLive ? (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={(e) => { e.stopPropagation(); navigate(`/pool/${pool.id}/leaderboard`); }}
                        >
                          Leaderboard
                        </button>
                      ) : (
                        <span className="dash-chevron" aria-hidden="true">
                          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Community Pulse */}
        {eventPulse?.topFighter && eventPulse.totalPicks > 0 && (
          <section className="dash-area-pulse pnp-rise" style={{ '--i': 4 }}>
            <div className="dash-block-head">
              <p className="pnp-eyebrow pnp-eyebrow-mute">Community Pulse</p>
            </div>
            <div className="dash-pulse">
              <div className="dash-pulse-pct">{eventPulse.topPercent}%</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="dash-pulse-name">{eventPulse.topFighter}</div>
                <div className="dash-pulse-meta">
                  <span className="pnp-num">{eventPulse.totalPicks.toLocaleString()}</span> picks made · main event consensus
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
    </PullToRefresh>
  );
}

const POOL_ACCENTS = ['var(--primary)', 'var(--gold)', 'var(--cyan)', '#4f7cff'];
const poolAccentColor = (poolId) => POOL_ACCENTS[poolId % POOL_ACCENTS.length];

function StatCell({ label, value, accent }) {
  return (
    <div className={`pnp-stat ${accent ? 'dash-stat-accent' : ''}`}>
      <span className="pnp-stat-value">{value}</span>
      <span className="pnp-stat-label">{label}</span>
    </div>
  );
}

export default Dashboard;
